import React, { useState, useEffect } from "react";
import { useAuth, API, formatApiErrorDetail } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { ShoppingBag, Clock, User, Calendar, Plus, Trash2, Hand, Loader2 } from "lucide-react";

const Marketplace = () => {
  const { user, getAuthHeaders } = useAuth();
  const [posts, setPosts] = useState([]);
  const [myShifts, setMyShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPostDialog, setShowPostDialog] = useState(false);
  const [selectedShiftToPost, setSelectedShiftToPost] = useState("");
  const [postMessage, setPostMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [postsRes, shiftsRes] = await Promise.all([
        axios.get(`${API}/marketplace`, {
          headers: getAuthHeaders(),
          
        }),
        axios.get(`${API}/shifts/my`, {
          headers: getAuthHeaders(),
          
        }),
      ]);
      setPosts(postsRes.data);
      // Filter to only future shifts
      const today = format(new Date(), "yyyy-MM-dd");
      setMyShifts(shiftsRes.data.filter((s) => s.date >= today));
    } catch (error) {
      console.error("Error fetching marketplace data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePostShift = async () => {
    if (!selectedShiftToPost) {
      toast.error("Please select a shift to post");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/marketplace`,
        {
          shift_id: selectedShiftToPost,
          message: postMessage,
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Shift posted to marketplace!");
      setShowPostDialog(false);
      setSelectedShiftToPost("");
      setPostMessage("");
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to post shift");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemovePost = async (postId) => {
    try {
      await axios.delete(`${API}/marketplace/${postId}`, {
        headers: getAuthHeaders(),
        
      });
      toast.success("Post removed from marketplace");
      fetchData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to remove post");
    }
  };

  const handleRequestShift = async (postId) => {
    try {
      await axios.post(
        `${API}/marketplace/${postId}/request`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success("Request submitted! The poster will be notified.");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to request shift");
    }
  };

  const myPostedShiftIds = posts.filter((p) => p.user_id === user?.id).map((p) => p.shift_id);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="marketplace-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1A1A1A]">Shift Marketplace</h1>
          <p className="text-[#666666]">Find and offer shifts for swap</p>
        </div>
        <Button
          onClick={() => setShowPostDialog(true)}
          className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
          data-testid="post-shift-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Post My Shift
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-[#666666]">Loading marketplace...</div>
        </div>
      ) : posts.length === 0 ? (
        <Card className="border border-[#E5E5E5] shadow-none">
          <CardContent className="py-12 text-center">
            <ShoppingBag className="w-12 h-12 mx-auto text-[#999999] mb-4" />
            <p className="text-[#666666]">No shifts available on the marketplace</p>
            <p className="text-sm text-[#999999] mt-1">Be the first to post a shift!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {posts.map((post) => {
            const isOwner = post.user_id === user?.id;
            return (
              <Card
                key={post.id}
                className={`border shadow-none transition-all ${
                  isOwner
                    ? "border-[#6A1E2E] bg-[#F0EAEB]/30"
                    : "border-[#E5E5E5] hover:border-[#6A1E2E]"
                }`}
                data-testid={`marketplace-post-${post.id}`}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-semibold text-[#1A1A1A]">
                    {post.shift?.event_name || "Shift"}
                  </CardTitle>
                  <div className="flex items-center gap-2 text-sm text-[#666666]">
                    <User className="w-3 h-3" />
                    <span>{post.poster_name}</span>
                    {isOwner && (
                      <span className="text-xs bg-[#6A1E2E] text-white px-2 py-0.5 rounded-sm">
                        Your Post
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {post.shift && (
                    <>
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-[#6A1E2E]" />
                        <span>{format(parseISO(post.shift.date), "EEEE, MMM d, yyyy")}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-[#6A1E2E]" />
                        <span className="font-mono">
                          {post.shift.start_time} - {post.shift.end_time}
                        </span>
                      </div>
                    </>
                  )}
                  {post.message && (
                    <p className="text-sm text-[#666666] bg-[#FAFAFA] p-2 rounded-sm">
                      "{post.message}"
                    </p>
                  )}
                  <div className="pt-2">
                    {isOwner ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRemovePost(post.id)}
                        className="w-full border-red-300 text-red-600 hover:bg-red-50"
                        data-testid={`remove-post-${post.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remove Post
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleRequestShift(post.id)}
                        className="w-full bg-[#6A1E2E] hover:bg-[#521422] text-white"
                        data-testid={`request-shift-${post.id}`}
                      >
                        <Hand className="w-4 h-4 mr-2" />
                        I'll Take It
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Post Shift Dialog */}
      <Dialog open={showPostDialog} onOpenChange={setShowPostDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Post Shift to Marketplace</DialogTitle>
            <DialogDescription>
              Select a shift you want others to take. You'll be notified when someone requests it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#1A1A1A]">Select Shift</label>
              <Select value={selectedShiftToPost} onValueChange={setSelectedShiftToPost}>
                <SelectTrigger className="border-[#E5E5E5]" data-testid="select-shift-to-post">
                  <SelectValue placeholder="Choose a shift to post" />
                </SelectTrigger>
                <SelectContent>
                  {myShifts.filter((s) => !myPostedShiftIds.includes(s.id)).length === 0 ? (
                    <SelectItem value="none" disabled>
                      No shifts available to post
                    </SelectItem>
                  ) : (
                    myShifts
                      .filter((s) => !myPostedShiftIds.includes(s.id))
                      .map((shift) => (
                        <SelectItem key={shift.id} value={shift.id}>
                          {shift.event_name} - {format(parseISO(shift.date), "MMM d")} (
                          {shift.start_time}-{shift.end_time})
                        </SelectItem>
                      ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#1A1A1A]">Message (Optional)</label>
              <Textarea
                placeholder="Add a note for potential takers..."
                value={postMessage}
                onChange={(e) => setPostMessage(e.target.value)}
                className="border-[#E5E5E5] min-h-[80px]"
                data-testid="post-message-input"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setShowPostDialog(false)}
              className="border-[#E5E5E5]"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePostShift}
              disabled={submitting || !selectedShiftToPost}
              className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
              data-testid="confirm-post-shift"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Post to Marketplace"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Marketplace;
